import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  InkFormData,
  InkHistoryEntry,
  InkStroke,
  createEmptyInkFormData
} from '../models/draft-form.model';

/**
 * Centralized stroke storage service.
 * Provided per InspectionDraftComponent instance (not root).
 */
@Injectable()
export class DraftStateService {
  private fieldsMap = new Map<string, BehaviorSubject<InkStroke[]>>();
  private fieldTitles = new Map<string, string>();
  private dirtyFields = new Set<string>();
  private lastStroke: InkHistoryEntry | null = null;
  private _isDirty = false;

  /** Emits when a stroke is added (for auto-save debouncing) */
  readonly strokeAdded$ = new Subject<void>();

  get canUndo(): boolean {
    return this.lastStroke !== null;
  }

  get isDirty(): boolean {
    return this._isDirty;
  }

  set isDirty(value: boolean) {
    this._isDirty = value;
  }

  /** Register a Hebrew title for a field (called by directive on init) */
  setFieldTitle(fieldId: string, title: string): void {
    this.fieldTitles.set(fieldId, title);
  }

  /** Get strokes for a specific field (synchronous) */
  getStrokes(fieldId: string): InkStroke[] {
    return this.fieldsMap.get(fieldId)?.value || [];
  }

  /** Observable of strokes for a field (directive subscribes to this) */
  getStrokes$(fieldId: string): Observable<InkStroke[]> {
    return this.getOrCreateSubject(fieldId).asObservable();
  }

  /** Add a completed stroke to a field */
  addStroke(fieldId: string, stroke: InkStroke): void {
    const subject = this.getOrCreateSubject(fieldId);
    subject.next([...subject.value, stroke]);
    this.lastStroke = { fieldId, strokeId: stroke.id };
    this._isDirty = true;
    this.dirtyFields.add(fieldId);
    this.strokeAdded$.next();
  }

  /** Undo the last stroke (global — removes from whichever field it was drawn on) */
  undo(): void {
    if (!this.lastStroke) {
      return;
    }

    const { fieldId, strokeId } = this.lastStroke;
    const subject = this.fieldsMap.get(fieldId);
    if (!subject) {
      return;
    }

    const filtered = subject.value.filter((s) => s.id !== strokeId);
    subject.next(filtered);
    this.lastStroke = null; // Only one level of undo
    this._isDirty = true;
    this.dirtyFields.add(fieldId);
  }

  /** Clear all strokes for a field (keeps the subject alive so the directive re-renders) */
  clearField(fieldId: string): void {
    const subject = this.fieldsMap.get(fieldId);
    if (subject) {
      subject.next([]);
      this.dirtyFields.add(fieldId);
      this._isDirty = true;
    }
    if (this.lastStroke?.fieldId === fieldId) {
      this.lastStroke = null;
    }
  }

  /** Remove all strokes for a field (when user deletes a dynamic row field) */
  removeField(fieldId: string): void {
    const subject = this.fieldsMap.get(fieldId);
    if (subject) {
      subject.complete();
      this.fieldsMap.delete(fieldId);
    }
    if (this.lastStroke?.fieldId === fieldId) {
      this.lastStroke = null;
    }
  }

  /** Remove strokes for all fields matching a prefix (when removing a dynamic row) */
  removeFieldsByPrefix(prefix: string): void {
    const keysToRemove: string[] = [];
    this.fieldsMap.forEach((_, key) => {
      if (key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    });
    keysToRemove.forEach((key) => this.removeField(key));
  }

  /** Load ink data from backend (on init) */
  loadInkData(data: InkFormData): void {
    if (!data || !data.fields) {
      return;
    }

    Object.entries(data.fields).forEach(([fId, fieldData]) => {
      if (fieldData.strokes?.length) {
        const subject = this.getOrCreateSubject(fId);
        subject.next(fieldData.strokes);
      }
    });

    if (data.fieldTitles) {
      Object.entries(data.fieldTitles).forEach(([fId, title]) => {
        this.fieldTitles.set(fId, title);
      });
    }

    this._isDirty = false;
  }

  /** Whether any fields have been modified since the last save */
  get hasDirtyFields(): boolean {
    return this.dirtyFields.size > 0;
  }

  /** Serialize only changed fields for delta save */
  serializeDelta(): InkFormData {
    const data = createEmptyInkFormData();

    this.dirtyFields.forEach((fId) => {
      // Skip noteInput fields — they are temporary drawing areas for ink notes.
      // Their strokes are committed via addInkNote() and should not be persisted as field ink.
      if (fId.endsWith(':noteInput')) return;

      const subject = this.fieldsMap.get(fId);
      // Include field even if empty (tells backend to clear it)
      data.fields[fId] = { strokes: subject?.value || [] };
    });

    const fieldTitles: Record<string, string> = {};
    this.fieldTitles.forEach((title, fId) => {
      fieldTitles[fId] = title;
    });
    data.fieldTitles = fieldTitles;

    if (this.lastStroke) {
      data.lastStroke = { ...this.lastStroke };
    }

    return data;
  }

  /** Mark all dirty fields as clean after a successful save */
  markClean(): void {
    this.dirtyFields.clear();
    this._isDirty = false;
  }

  /** Serialize current state for backend save */
  serializeInkData(): InkFormData {
    const data = createEmptyInkFormData();

    this.fieldsMap.forEach((subject, fId) => {
      // Skip noteInput fields — temporary drawing areas, not persisted as field ink
      if (fId.endsWith(':noteInput')) return;

      if (subject.value.length > 0) {
        data.fields[fId] = { strokes: subject.value };
      }
    });

    const fieldTitles: Record<string, string> = {};
    this.fieldTitles.forEach((title, fId) => {
      fieldTitles[fId] = title;
    });
    data.fieldTitles = fieldTitles;

    if (this.lastStroke) {
      data.lastStroke = { ...this.lastStroke };
    }

    return data;
  }

  private getOrCreateSubject(fieldId: string): BehaviorSubject<InkStroke[]> {
    let subject = this.fieldsMap.get(fieldId);
    if (!subject) {
      subject = new BehaviorSubject<InkStroke[]>([]);
      this.fieldsMap.set(fieldId, subject);
    }
    return subject;
  }
}
